import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ClockIcon, ShieldIcon, XCircleIcon, LockIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { FORM } from '../../content/site';
import { EASE, Eyebrow } from '../ui/Primitives';
const TRUST_ICONS: Record<string, React.ElementType> = {
  clock: ClockIcon,
  shield: ShieldIcon,
  x: XCircleIcon
};
const inputClass = 'w-full rounded-xl border border-espresso/15 bg-white px-4 py-3.5 text-sm font-light text-espresso outline-none transition-all placeholder:text-espresso/30 focus:border-bronze focus:ring-2 focus:ring-bronze/15';
const labelClass = 'mb-2 block text-[10px] font-semibold uppercase tracking-[0.16em] text-espresso/55';
export function ConsultationForm() {
  const {
    language
  } = useLanguage();
  const t = FORM[language];
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    language: '',
    service: '',
    message: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  if (submitted) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.97
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.5,
      ease: EASE
    }} className="mx-auto max-w-xl rounded-2xl border border-espresso/10 bg-white p-10 text-center shadow-soft lg:p-14" data-id="element-28" data-mp="v6q6b7329xyn">
        <div className="mx-auto mb-7 flex h-14 w-14 items-center justify-center rounded-full border border-bronze/40" data-id="element-29" data-mp="q7djgmc6wqw2">
          <CheckIcon className="h-6 w-6 text-bronze" strokeWidth={1.5} data-id="element-30" data-mp="36vhmzig0c6go" />
        </div>
        <h3 className="mb-4 font-serif text-3xl font-light text-espresso" data-id="element-31" data-mp="3axcooo1uoluz">
          {t.success.title}
        </h3>
        <p className="text-sm font-light leading-relaxed text-espresso/65" data-id="element-32" data-mp="1hff62f76s02v">
          {t.success.message}
        </p>
        <button type="button" onClick={() => setSubmitted(false)} className="mt-8 border-b border-bronze/50 pb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-espresso/70 transition-colors hover:text-espresso" data-id="element-33" data-mp="1cgu2q6wn10a7">
          {t.success.again}
        </button>
      </motion.div>;
  }
  return <div className="grid gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-34" data-mp="ym1g6oibyju4">
      <motion.div initial={{
      opacity: 0,
      y: 24
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-60px'
    }} transition={{
      duration: 0.7,
      ease: EASE
    }} className="lg:col-span-5" data-id="element-35" data-mp="toi3vu570h4t">
        <Eyebrow data-id="element-36" data-mp="2j4923ow91bbw">{t.eyebrow}</Eyebrow>
        <h2 className="mt-5 font-serif text-3xl font-light leading-[1.1] tracking-tight text-espresso sm:text-4xl" data-id="element-37" data-mp="2j36crlekrgy3">
          {t.title}
        </h2>
        <p className="mt-5 text-base font-light leading-relaxed text-espresso/60" data-id="element-38" data-mp="2e3bnfmag6zf5">
          {t.subtitle}
        </p>

        <ul className="mt-10 space-y-5 border-t border-espresso/10 pt-8" data-id="element-39" data-mp="2qnic9kckxe20">
          {t.trust.map(item => {
          const Icon = TRUST_ICONS[item.icon] || ShieldIcon;
          return <li key={item.text} className="flex items-center gap-4" data-id="element-40" data-mp="30daacojw0pb8">
                <Icon className="h-4 w-4 shrink-0 text-bronze" strokeWidth={1.5} data-id="element-41" data-mp="30fo2aoj9tlek" />
                <span className="text-sm font-light text-espresso/75" data-id="element-42" data-mp="27omn4nfkvpv1">
                  {item.text}
                </span>
              </li>;
        })}
        </ul>

        <div className="mt-10 flex items-start gap-3 rounded-xl border border-espresso/10 bg-sand/50 p-5" data-id="element-43" data-mp="2cqbthpruvfcs">
          <LockIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bronze" strokeWidth={1.5} data-id="element-44" data-mp="3o34iwtx6brpo" />
          <p className="text-xs font-light leading-relaxed text-espresso/60" data-id="element-45" data-mp="3t1fupa714caj">
            {t.privacyNote}
          </p>
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 24
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-60px'
    }} transition={{
      duration: 0.7,
      delay: 0.12,
      ease: EASE
    }} className="lg:col-span-7" data-id="element-46" data-mp="15vw1krz0ohln">
        <form onSubmit={handleSubmit} className="space-y-6 rounded-2xl border border-espresso/10 bg-white p-7 shadow-soft sm:p-10" data-id="element-47" data-mp="19ubwdi1cau9m">
          <div className="grid gap-6 sm:grid-cols-2" data-id="element-48" data-mp="1jtswzl1qihyq">
            <div data-id="element-49" data-mp="1jt1b51xxvyln">
              <label htmlFor="name" className={labelClass} data-id="element-50" data-mp="1jrghrfnd2lpd">
                {t.fields.name} *
              </label>
              <input id="name" type="text" name="name" required autoComplete="name" value={formData.name} onChange={handleChange} className={inputClass} data-id="element-51" data-mp="1tjcfgpljc9t2" />
            </div>
            <div data-id="element-52" data-mp="2m63voacczad4">
              <label htmlFor="phone" className={labelClass} data-id="element-53" data-mp="2m5kit81kfc5y">
                {t.fields.phone} *
              </label>
              <input id="phone" type="tel" name="phone" required autoComplete="tel" value={formData.phone} onChange={handleChange} className={inputClass} data-id="element-54" data-mp="2r3m1hrv2x9gw" />
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2" data-id="element-55" data-mp="23di1duh3nrun">
            <div data-id="element-56" data-mp="2874efnr6dxqz">
              <label htmlFor="language" className={labelClass} data-id="element-57" data-mp="287m8qeua21w9">
                {t.fields.language}
              </label>
              <select id="language" name="language" value={formData.language} onChange={handleChange} className={inputClass} data-id="element-58" data-mp="1tf2cly5jouob">
                <option value="" data-id="element-59" data-mp="1ohu28jqn1uxm">{t.select}</option>
                {t.languageOptions.map(opt => <option key={opt} value={opt} data-id="element-60" data-mp="3r4h97ys7ec9h">
                    {opt}
                  </option>)}
              </select>
            </div>
            <div data-id="element-61" data-mp="3mcpqgafahatw">
              <label htmlFor="service" className={labelClass} data-id="element-62" data-mp="3mc7jojv03e7q">
                {t.fields.service}
              </label>
              <select id="service" name="service" value={formData.service} onChange={handleChange} className={inputClass} data-id="element-63" data-mp="3cg4h5cw8ldk9">
                <option value="" data-id="element-64" data-mp="8yunr1s8jjx5">{t.select}</option>
                {t.serviceOptions.map(opt => <option key={opt} value={opt} data-id="element-65" data-mp="3z00r9bkzehd">
                    {opt}
                  </option>)}
              </select>
            </div>
          </div>

          <div data-id="element-66" data-mp="2oq0dx6mynfts">
            <label htmlFor="message" className={labelClass} data-id="element-67" data-mp="2oqijwuzfd8ew">
              {t.fields.message}
            </label>
            <textarea id="message" name="message" rows={4} value={formData.message} onChange={handleChange} className={`${inputClass} resize-none`} data-id="element-68" data-mp="2knvpcmbdd9e0" />
          </div>

          <button type="submit" className="w-full rounded-full bg-espresso px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-cream transition-all hover:bg-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-bronze focus-visible:ring-offset-2" data-id="element-69" data-mp="197knzo8pqrgb">
            {t.submit}
          </button>

          <p className="text-center text-xs font-light leading-relaxed text-espresso/50" data-id="element-70" data-mp="1nunmny8rv658">
            {t.disclaimer}
          </p>
        </form>
      </motion.div>
    </div>;
}